import React, { useMemo } from 'react';
import {
    TextField, Grid, FormControl,
    InputLabel, Select, MenuItem, Typography,
    InputAdornment, Checkbox, FormControlLabel,
    FormHelperText,
    Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors, UseFormRegister, useWatch } from 'react-hook-form';
import { TipoLimiteEnum } from 'repository/timekeeper/@types/TypeLimitEnum';
import { TipoCheckLimitEnum } from 'repository/timekeeper/@types/TypeCheckLimitEnum';
import { TypeShiftLimitEnum } from 'repository/timekeeper/@types/TypeShiftLimitEnum';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
import { LimitFormValues } from './limitFormSchema';

interface LimitFormFieldsProps {
    control: Control<LimitFormValues>;
    register: UseFormRegister<LimitFormValues>;
    errors: FieldErrors<LimitFormValues>;
    clearErrors: (name?: keyof LimitFormValues) => void;
    loading: boolean;
    nameValue: string;
    descriptionValue?: string;
    datePatternOptions: any[];
}

export const LimitFormFields: React.FC<LimitFormFieldsProps> = ({
    control, register, errors, clearErrors, loading, nameValue, descriptionValue, datePatternOptions
}) => {
    const { t } = useTranslation();

    const limitType = useWatch({ control, name: 'type_limit' });
    const checkLimitType = useWatch({ control, name: 'type_check_limit' });

    const typeLimitOptions = useMemo(() => [
        { value: TipoLimiteEnum.CANTIDAD_GANANCIA, label: t('accruals.limits.dialog.inputs.option_type_limit.profit_amount_limit') },
        { value: TipoLimiteEnum.BALANCE_GANANCIA, label: t('accruals.limits.dialog.inputs.option_type_limit.profit_balance_limit') },
        { value: TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION, label: t('accruals.limits.dialog.inputs.option_type_limit.accumulation_displacement_limit') },
        { value: TipoLimiteEnum.HORAS_ACUMULADAS, label: t('accruals.limits.dialog.inputs.option_type_limit.accumulated_hours_limit_minus_the_use_of_accumulation_balances') },
        { value: TipoLimiteEnum.USO_BALANCES, label: t('accruals.limits.dialog.inputs.option_type_limit.limit_on_the_use_of_balances') },
    ], [t]);

    const checkLimitOptions = useMemo(() => [
        { value: TipoCheckLimitEnum.TEMPORADA, label: t('accruals.limits.dialog.inputs.option_type_check_limit.check_limit_during_season') },
        { value: TipoCheckLimitEnum.CONTINUO, label: t('accruals.limits.dialog.inputs.option_type_check_limit.check_limit_during_continuous_operation') },
        { value: TipoCheckLimitEnum.PATRON, label: t('accruals.limits.dialog.inputs.option_type_check_limit.check_limit_in_pattern') },
    ], [t]);

    const shiftLimitOptions = useMemo(() => [
        { value: TypeShiftLimitEnum.MAXIMO, label: t('accruals.limits.dialog.inputs.options_type_shift_limit.maximum') },
        { value: TypeShiftLimitEnum.MAXIMO_PERMITIR_BALANCES_NEGATIVO, label: t('accruals.limits.dialog.inputs.options_type_shift_limit.maximum_allowed_negative_balances') },
        { value: TypeShiftLimitEnum.MINIMO, label: t('accruals.limits.dialog.inputs.options_type_shift_limit.minimum') },
    ], [t]);

    const MonthOfYear = useMemo(() => [
        { value: 1, label: t('general.months.january') },
        { value: 2, label: t('general.months.february') },
        { value: 3, label: t('general.months.march') },
        { value: 4, label: t('general.months.april') },
        { value: 5, label: t('general.months.may') },
        { value: 6, label: t('general.months.june') },
        { value: 7, label: t('general.months.july') },
        { value: 8, label: t('general.months.august') },
        { value: 9, label: t('general.months.september') },
        { value: 10, label: t('general.months.october') },
        { value: 11, label: t('general.months.november') },
        { value: 12, label: t('general.months.december') },
    ], [t]);

    const frequencyOptions = useMemo(() => [
        { value: FrequencyUnitTime.DIAS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.days') },
        { value: FrequencyUnitTime.SEMANAS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.weeks') },
        { value: FrequencyUnitTime.MESES, label: t('accruals.date_patterns.dialog.inputs.options_frequency.months') },
        { value: FrequencyUnitTime.ANOS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.years') },
        { value: FrequencyUnitTime.PERIODO_PAGO, label: t('accruals.date_patterns.dialog.inputs.options_frequency.periods_of_pay') },
    ], [t]);

    return (
        <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    fullWidth
                    label={t('accruals.limits.dialog.inputs.name')}
                    autoComplete='off'
                    disabled={loading}
                    {...register('name')}
                    error={!!errors.name}
                    slotProps={{
                        htmlInput: { maxLength: 100 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${nameValue?.length || 0}/100`}</InputAdornment>
                        }
                    }}
                    required
                    onBlur={() => clearErrors('name')}
                />
                <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }} component={'span'}>
                    <Box component={'span'} color={(theme) => theme.palette.error.main}>{errors.name?.message}</Box>
                </FormHelperText>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    fullWidth
                    label={t('accruals.limits.dialog.inputs.description')}
                    autoComplete='off'
                    {...register('description')}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    multiline
                    rows={2}
                    slotProps={{
                        htmlInput: { maxLength: 255 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${descriptionValue?.length || 0}/255`}</InputAdornment>,
                        },
                    }}
                    disabled={loading}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Controller
                    name="type_limit"
                    control={control}
                    render={({ field }) => (
                        <FormControl fullWidth required>
                            <InputLabel id="limit-type-label">{t('accruals.limits.dialog.inputs.limit_type')}</InputLabel>
                            <Select
                                {...field}
                                labelId="limit-type-label"
                                label={t('accruals.limits.dialog.inputs.limit_type')}
                                disabled={loading}
                            >
                                {typeLimitOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Controller
                    name="modify_by_etc"
                    control={control}
                    render={({ field }) => (
                        <FormControl fullWidth>
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value} />}
                                label={t('accruals.limits.dialog.inputs.modify_by_etc')}
                                disabled={loading}
                            />
                        </FormControl>
                    )}
                />
            </Grid>
            {limitType !== TipoLimiteEnum.BALANCE_GANANCIA && limitType !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION && (
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Controller
                        name="type_check_limit"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth required>
                                <InputLabel id="check-limit-label">{t('accruals.limits.dialog.inputs.check_limit')}</InputLabel>
                                <Select
                                    {...field}
                                    labelId="check-limit-label"
                                    label={t('accruals.limits.dialog.inputs.check_limit')}
                                    disabled={loading}
                                >
                                    {checkLimitOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>
            )}

            {/* Opción 1: Temporada */}
            {checkLimitType === TipoCheckLimitEnum.TEMPORADA
                && limitType !== TipoLimiteEnum.BALANCE_GANANCIA
                && limitType !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION
                && (
                    <>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <Typography variant="body2">{t('accruals.limits.dialog.inputs.of_day_month')}</Typography>
                            <Grid container direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                    <Controller
                                        name="start_month"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth>
                                                <InputLabel id="from-month-label">{t('accruals.limits.dialog.inputs.month')}</InputLabel>
                                                <Select
                                                    {...field}
                                                    labelId="from-month-label"
                                                    label={t('accruals.limits.dialog.inputs.month')}
                                                    disabled={loading}
                                                >
                                                    {MonthOfYear.map((month) => (
                                                        <MenuItem key={month.value} value={month.value}>
                                                            {month.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                    <Controller
                                        name="start_day"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth>
                                                <InputLabel id="from-day-label">{t('accruals.limits.dialog.inputs.day')}</InputLabel>
                                                <Select
                                                    {...field}
                                                    labelId="from-day-label"
                                                    label={t('accruals.limits.dialog.inputs.day')}
                                                    disabled={loading}
                                                >
                                                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                                        <MenuItem key={day} value={day}>{day}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <Typography variant="body2">{t('accruals.limits.dialog.inputs.to_day_month')}</Typography>
                            <Grid container direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                    <Controller
                                        name="end_month"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth>
                                                <InputLabel id="to-month-label">{t('accruals.limits.dialog.inputs.month')}</InputLabel>
                                                <Select
                                                    {...field}
                                                    labelId="to-month-label"
                                                    label={t('accruals.limits.dialog.inputs.month')}
                                                    disabled={loading}
                                                >
                                                    {MonthOfYear.map((month) => (
                                                        <MenuItem key={month.value} value={month.value}>
                                                            {month.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                    <Controller
                                        name="end_day"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth>
                                                <InputLabel id="to-day-label">{t('accruals.limits.dialog.inputs.day')}</InputLabel>
                                                <Select
                                                    {...field}
                                                    labelId="to-day-label"
                                                    label={t('accruals.limits.dialog.inputs.day')}
                                                    disabled={loading}
                                                >
                                                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                                        <MenuItem key={day} value={day}>{day}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )
            }

            {/* Opción 2: Período continuo */}
            {checkLimitType === TipoCheckLimitEnum.CONTINUO
                && limitType !== TipoLimiteEnum.BALANCE_GANANCIA
                && limitType !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION
                && (
                    <>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TextField
                                type='number'
                                fullWidth
                                label={t('accruals.limits.dialog.inputs.frequency')}
                                autoComplete='off'
                                {...register('frecuency', { valueAsNumber: true })}
                                slotProps={{ htmlInput: { min: 0, max: 100 } }}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <Controller
                                name="type_unit_time"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel id="frequency-unit-time-label">{t('accruals.limits.dialog.inputs.frequency_amount')}</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="frequency-unit-time-label"
                                            label={t('accruals.limits.dialog.inputs.frequency_amount')}
                                            disabled={loading}
                                        >
                                            {frequencyOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </>
                )
            }

            {/* Opción 3: Patrón de fechas */}
            {checkLimitType === TipoCheckLimitEnum.PATRON
                && limitType !== TipoLimiteEnum.BALANCE_GANANCIA
                && limitType !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION
                && (
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Controller
                            name="date_pattern_id"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="date-pattern-label">{t('accruals.limits.dialog.inputs.date_pattern')}</InputLabel>
                                    <Select
                                        {...field}
                                        labelId="date-pattern-label"
                                        label={t('accruals.limits.dialog.inputs.date_pattern')}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                        disabled={loading}
                                    >
                                        {datePatternOptions.map((option) => (
                                            <MenuItem key={option.date_pattern_id} value={option.date_pattern_id}>
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>
                )
            }

            {/* Desplazamiento de acumulación */}
            {limitType === TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION && (
                <>
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <Controller
                            name="date_pattern_id"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="shift-limit-label">{t('accruals.limits.dialog.inputs.date_pattern')}</InputLabel>
                                    <Select
                                        {...field}
                                        label={t('accruals.limits.dialog.inputs.date_pattern')}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                        disabled={loading}
                                    >
                                        {datePatternOptions.map((option) => (
                                            <MenuItem key={option.date_pattern_id} value={option.date_pattern_id}>
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <Controller
                            name="type_shift_limit"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="shift-limit-type-label">{t('accruals.limits.dialog.inputs.type_shift_limit')}</InputLabel>
                                    <Select
                                        {...field}
                                        label={t('accruals.limits.dialog.inputs.type_shift_limit')}
                                        disabled={loading}
                                    >
                                        {shiftLimitOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
};
